import { Builder, BuilderComponent, builder } from '@builder.io/react';
import { graphql } from 'gatsby';
import { Suspense } from 'react';
import customComponentsRegistration, { richTextModels } from 'templates/componentGenerator';

import ModalContainer from 'molecules/Modal/Modal';

import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import ResourceHero from 'components/Hero/HeroResource/HeroResource';
import Main from 'components/Main';
import SEO from 'components/seo';

import type { Query } from 'graphqlTypes';
import type { FC } from 'react';
import type { IHead } from 'templates/BuilderPage';

builder.init(process.env.GATSBY_BUILDER_KEY || '');

const GatedResourceTemplate = (query: { data: Query }) => {
  const {
    data: {
      allBuilderModels: {
        oneGatedResource,
        oneGlobalBanner,
        oneComponentHeader: header,
        oneComponentFooter: footer,
        hiddenFormFields,
      },
    },
  } = query;

  const content = oneGatedResource?.content;

  const { banner } = { ...oneGatedResource?.data };
  const globalBanner = oneGlobalBanner?.data;

  const isCustomBanner = banner?.customBanner === true;
  const isGlobalBanner = globalBanner?.showBanner === true;
  const isHideBanner = banner?.hideBanner === true;
  const includePlaybook = oneGatedResource?.data?.includePlaybookSection;
  const playbook = oneGatedResource?.data?.playbookContent;

  customComponentsRegistration();

  return (
    <Suspense fallback>
      {header?.content && (
        <Header
          {...header.content.data}
          banner={(isCustomBanner && !isHideBanner && banner) || (isGlobalBanner && !isHideBanner && globalBanner)}
        />
      )}
      <Main>
        <ResourceHero resource={content} playbook={includePlaybook} />
        <BuilderComponent
          model="gated-resource"
          data={{ template: 'gatedResource', hiddenFields: JSON.stringify({ hiddenFormFields }) }}
          content={content}
          context={{ includePlaybookSection: includePlaybook, playbookContent: playbook }}
          options={{ includeRefs: true, cachebust: true }}
        />
      </Main>
      {footer?.content && <Footer isDark={true} content={footer.content.data} />}
      <ModalContainer />
    </Suspense>
  );
};

export default GatedResourceTemplate;

export const Head: FC<IHead> = ({
  data: {
    allBuilderModels: { oneGatedResource },
  },
  location,
}) => (
  <>
    {oneGatedResource && (
      <SEO
        title={oneGatedResource?.data?.metaTitle || oneGatedResource?.data?.title}
        description={oneGatedResource?.data?.metaDescription || oneGatedResource?.data?.description}
        image={oneGatedResource?.data?.featuredImage}
        location={location}
        indexable={!oneGatedResource?.data?.noIndex || true}
      />
    )}
  </>
);

export const gatedResourceQuery = graphql`
  query getGatedResources($path: String!) {
    allBuilderModels {
      oneGatedResource(target: { urlPath: $path }, options: { includeRefs: true, cachebust: true }) {
        content
        data {
          title
          description
          featuredImage
          url
          noIndex
          metaTitle
          metaDescription
          includePlaybookSection
          playbookContent
        }
      }
      oneComponentHeader(
        options: { includeRefs: true, cachebust: true }
        target: { id: "8fe1129198a9478c9cc7346a40a63805" }
      ) {
        content
      }
      oneComponentFooter(
        options: { includeRefs: true, cachebust: true }
        target: { id: "a7c491a16336403c94432e7b3ebb419b" }
      ) {
        content
      }
      oneGlobalBanner(
        options: { includeRefs: true, cachebust: true }
        target: { id: "b241c59d043b4cc7b0f608356bea42ef" }
      ) {
        data {
          showBanner
          body
          button
          heading
          includeIcon
        }
      }
      hiddenFormFields {
        data {
          hiddenFormField
        }
      }
    }
  }
`;

Builder.register('insertMenu', {
  name: 'Gated Resources',
  advanced: true,
  items: richTextModels.map(model => ({ name: model.config.name })),
});
